<script lang="ts">
  import TeamPanel from '../common/team-panel.svelte'
  import { teamStats } from './stores'
  import {
    getDisplay,
    getValue,
    type GameraTeamReference,
    type NflPlayEvent,
  } from '@statmuse/core/gamera'
  import { drives, events } from './stores'
  import { round } from 'lodash-es'

  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined

  // const compare = <K extends NflStatKey>(
  //   x: StatModel<K>,
  //   y: StatModel<K>,
  //   k: K,
  //   c: 'gt' | 'lt' = 'gt',
  // ) => {
  //   return c === 'gt'
  //     ? getValue(x, k) >= getValue(y, k)
  //     : getValue(x, k) < getValue(y, k)
  // }

  $: awayTeamStats =
    $teamStats.away?.statsLookup[$teamStats.away?.statsLookupKey ?? '']
  $: homeTeamStats =
    $teamStats.home?.statsLookup[$teamStats.home?.statsLookupKey ?? '']

  $: awayDrives = $drives.filter((d) => {
    const firstEvent = $events.find(
      (e) => e.driveSequence === d.driveSequence && e.type === 'play',
    ) as NflPlayEvent | undefined
    const situation =
      firstEvent?.playType === 'kickoff' || firstEvent?.playType === 'freeKick'
        ? firstEvent.endSituation
        : firstEvent?.startSituation
    return situation?.possessionTeamId === awayTeam?.teamId
  })

  $: homeDrives = $drives.filter((d) => {
    const firstEvent = $events.find(
      (e) => e.driveSequence === d.driveSequence && e.type === 'play',
    ) as NflPlayEvent | undefined
    const situation =
      firstEvent?.playType === 'kickoff' || firstEvent?.playType === 'freeKick'
        ? firstEvent.endSituation
        : firstEvent?.startSituation
    return situation?.possessionTeamId === homeTeam?.teamId
  })

  $: awayPassPlays =
    getValue(awayTeamStats ?? {}, 'Passing-Attempts') +
    getValue(awayTeamStats ?? {}, 'Passing-Sacks')

  $: homePassPlays =
    getValue(homeTeamStats ?? {}, 'Passing-Attempts') +
    getValue(homeTeamStats ?? {}, 'Passing-Sacks')

  $: awayRushPlays = getValue(awayTeamStats ?? {}, 'Rushing-Attempts')

  $: homeRushPlays = getValue(homeTeamStats ?? {}, 'Rushing-Attempts')

  $: awayTotalPlayCount = awayPassPlays + awayRushPlays
  $: homeTotalPlayCount = homePassPlays + homeRushPlays

  $: awayRushPerPlay = round(
    awayRushPlays > 0
      ? getValue(awayTeamStats ?? {}, 'Rushing-Yards') / awayRushPlays
      : 0,
    1,
  )
  $: homeRushPerPlay = round(
    awayRushPlays > 0
      ? getValue(homeTeamStats ?? {}, 'Rushing-Yards') / homeRushPlays
      : 0,
    1,
  )

  $: awayPassPerPlay = round(
    awayPassPlays > 0
      ? (getValue(awayTeamStats ?? {}, 'Passing-Yards') -
          getValue(awayTeamStats ?? {}, 'Passing-SackYards')) /
          awayPassPlays
      : 0,
    1,
  )
  $: homePassPerPlay = round(
    awayPassPlays > 0
      ? (getValue(homeTeamStats ?? {}, 'Passing-Yards') -
          getValue(homeTeamStats ?? {}, 'Passing-SackYards')) /
          homePassPlays
      : 0,
    1,
  )

  $: awayYardsPerPlay = round(
    awayTotalPlayCount > 0
      ? (getValue(awayTeamStats ?? {}, 'Passing-Yards') +
          getValue(awayTeamStats ?? {}, 'Rushing-Yards') -
          getValue(awayTeamStats ?? {}, 'Passing-SackYards')) /
          awayTotalPlayCount
      : 0,
    1,
  )
  $: homeYardsPerPlay = round(
    homeTotalPlayCount > 0
      ? (getValue(homeTeamStats ?? {}, 'Passing-Yards') +
          getValue(homeTeamStats ?? {}, 'Rushing-Yards') -
          getValue(homeTeamStats ?? {}, 'Passing-SackYards')) /
          homeTotalPlayCount
      : 0,
    1,
  )

  $: biggestLead = $events
    .filter((e) => e.type === 'play')
    .reduce(
      (acc, e) => {
        return {
          away: Math.max(acc.away, e.awayPoints - e.homePoints),
          home: Math.max(acc.home, e.homePoints - e.awayPoints),
        }
      },
      { away: 0, home: 0 },
    )

  $: awayTotalYds =
    getValue(awayTeamStats ?? {}, 'Passing-Yards') +
    getValue(awayTeamStats ?? {}, 'Rushing-Yards') -
    getValue(awayTeamStats ?? {}, 'Passing-SackYards')
  $: homeTotalYds =
    getValue(homeTeamStats ?? {}, 'Passing-Yards') +
    getValue(homeTeamStats ?? {}, 'Rushing-Yards') -
    getValue(homeTeamStats ?? {}, 'Passing-SackYards')

  $: awayNetPassingYds =
    getValue(awayTeamStats ?? {}, 'Passing-Yards') -
    getValue(awayTeamStats ?? {}, 'Passing-SackYards')
  $: homeNetPassingYds =
    getValue(homeTeamStats ?? {}, 'Passing-Yards') -
    getValue(homeTeamStats ?? {}, 'Passing-SackYards')

  $: awayTurnovers =
    getValue(awayTeamStats ?? {}, 'Passing-Interceptions') +
    getValue(awayTeamStats ?? {}, 'FumblesLost')
  $: homeTurnovers =
    getValue(homeTeamStats ?? {}, 'Passing-Interceptions') +
    getValue(homeTeamStats ?? {}, 'FumblesLost')

  $: awayDefensiveTd =
    getValue(awayTeamStats ?? {}, 'Defensive-InterceptionTouchdowns') +
    getValue(awayTeamStats ?? {}, 'Kickoff-ReturnTouchdowns') +
    getValue(awayTeamStats ?? {}, 'PuntReturn-Touchdowns')
  $: homeDefensiveTd =
    getValue(homeTeamStats ?? {}, 'Defensive-InterceptionTouchdowns') +
    getValue(homeTeamStats ?? {}, 'Kickoff-ReturnTouchdowns') +
    getValue(homeTeamStats ?? {}, 'PuntReturn-Touchdowns')
</script>

<TeamPanel {awayTeam} {homeTeam} title="Team Stats">
  <div
    class="border-t border-gray-6 dark:border-gray-4 divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1"
  >
    {#if getDisplay(awayTeamStats ?? {}, 'Passing-Yards') || getDisplay(homeTeamStats ?? {}, 'Passing-Yards') || getDisplay(awayTeamStats ?? {}, 'Rushing-Yards') || getDisplay(homeTeamStats ?? {}, 'Rushing-Yards')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={awayTotalYds > homeTotalYds
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {awayTotalYds}
        </p>
        <p class="text-gray-5">Total Yds</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={homeTotalYds > awayTotalYds
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {homeTotalYds}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Passing-Yards') || getDisplay(homeTeamStats ?? {}, 'Passing-Yards')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={awayNetPassingYds > homeNetPassingYds
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {awayNetPassingYds}
        </p>
        <p class="text-gray-5">Pass Yds</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={homeNetPassingYds > awayNetPassingYds
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {homeNetPassingYds}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Rushing-Yards') || getDisplay(homeTeamStats ?? {}, 'Rushing-Yards')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'Rushing-Yards') >
          getValue(homeTeamStats ?? {}, 'Rushing-Yards')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'Rushing-Yards')}
        </p>
        <p class="text-gray-5">Rush Yds</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'Rushing-Yards') >
          getValue(awayTeamStats ?? {}, 'Rushing-Yards')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'Rushing-Yards')}
        </p>
      </div>
    {/if}

    {#if awayDrives.length > 0 || homeDrives.length > 0}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={awayDrives.length > homeDrives.length
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {awayDrives.length}
        </p>
        <p class="text-gray-5">Total Drives</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={homeDrives.length > awayDrives.length
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {homeDrives.length}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Passing-Attempts') || getDisplay(homeTeamStats ?? {}, 'Passing-Attempts') || getDisplay(awayTeamStats ?? {}, 'Rushing-Attempts') || getDisplay(homeTeamStats ?? {}, 'Rushing-Attempts')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={awayTotalPlayCount > homeTotalPlayCount
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {awayTotalPlayCount}
        </p>
        <p class="text-gray-5">Total Plays</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={homeTotalPlayCount > awayTotalPlayCount
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {homeTotalPlayCount}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Passing-Attempts') || getDisplay(homeTeamStats ?? {}, 'Passing-Attempts') || getDisplay(awayTeamStats ?? {}, 'Rushing-Attempts') || getDisplay(homeTeamStats ?? {}, 'Rushing-Attempts')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={awayYardsPerPlay > homeYardsPerPlay
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {awayYardsPerPlay}
        </p>
        <p class="text-gray-5">Yards Per Play</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={homeYardsPerPlay > awayYardsPerPlay
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {homeYardsPerPlay}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Passing-Attempts') || getDisplay(homeTeamStats ?? {}, 'Passing-Attempts')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={awayPassPerPlay > homePassPerPlay
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {awayPassPerPlay}
        </p>
        <p class="text-gray-5">Yards Per Pass</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={homePassPerPlay > awayPassPerPlay
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {homePassPerPlay}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Rushing-Attempts') || getDisplay(homeTeamStats ?? {}, 'Rushing-Attempts')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={awayRushPerPlay > homeRushPerPlay
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {awayRushPerPlay}
        </p>
        <p class="text-gray-5">Yards Per Rush</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={homeRushPerPlay > awayRushPerPlay
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {homeRushPerPlay}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'TimeOfPossession') || getDisplay(homeTeamStats ?? {}, 'TimeOfPossession')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'TimeOfPossession') >
          getValue(homeTeamStats ?? {}, 'TimeOfPossession')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'TimeOfPossession')}
        </p>
        <p class="text-gray-5">Possession</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'TimeOfPossession') >
          getValue(awayTeamStats ?? {}, 'TimeOfPossession')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'TimeOfPossession')}
        </p>
      </div>
    {/if}

    {#if $events.length > 0}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={biggestLead.away > biggestLead.home
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {biggestLead.away}
        </p>
        <p class="text-gray-5">Biggest Lead</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={biggestLead.home > biggestLead.away
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {biggestLead.home}
        </p>
      </div>
    {/if}
  </div>
</TeamPanel>
<TeamPanel>
  <div
    class="border-t border-gray-6 dark:border-gray-4 divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1"
  >
    {#if getDisplay(awayTeamStats ?? {}, 'FirstDowns') || getDisplay(homeTeamStats ?? {}, 'FirstDowns')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'FirstDowns') >
          getValue(homeTeamStats ?? {}, 'FirstDowns')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'FirstDowns')}
        </p>
        <p class="text-gray-5">1st Downs</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'FirstDowns') >
          getValue(awayTeamStats ?? {}, 'FirstDowns')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'FirstDowns')}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'FirstDownPasses') || getDisplay(homeTeamStats ?? {}, 'FirstDownPasses')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'FirstDownPasses') >
          getValue(homeTeamStats ?? {}, 'FirstDownPasses')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'FirstDownPasses')}
        </p>
        <p class="text-gray-5">Passing 1st Downs</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'FirstDownPasses') >
          getValue(awayTeamStats ?? {}, 'FirstDownPasses')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'FirstDownPasses')}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'FirstDownRushes') || getDisplay(homeTeamStats ?? {}, 'FirstDownRushes')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'FirstDownRushes') >
          getValue(homeTeamStats ?? {}, 'FirstDownRushes')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'FirstDownRushes')}
        </p>
        <p class="text-gray-5">Rushing 1st Downs</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'FirstDownRushes') >
          getValue(awayTeamStats ?? {}, 'FirstDownRushes')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'FirstDownRushes')}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'FirstDownPenalties') || getDisplay(homeTeamStats ?? {}, 'FirstDownPenalties')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'FirstDownPenalties') >
          getValue(homeTeamStats ?? {}, 'FirstDownPenalties')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'FirstDownPenalties')}
        </p>
        <p class="text-gray-5">1st Downs from Penalties</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'FirstDownPenalties') >
          getValue(awayTeamStats ?? {}, 'FirstDownPenalties')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'FirstDownPenalties')}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'ThirdDownConversions') || getDisplay(homeTeamStats ?? {}, 'ThirdDownConversions')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'ThirdDownConversions') >
          getValue(homeTeamStats ?? {}, 'ThirdDownConversions')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {`${getDisplay(
            awayTeamStats ?? {},
            'ThirdDownConversions',
          )}/${getDisplay(awayTeamStats ?? {}, 'ThirdDownAttempts')}`}
        </p>
        <p class="text-gray-5">3rd Down</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'ThirdDownConversions') >
          getValue(awayTeamStats ?? {}, 'ThirdDownConversions')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {`${getDisplay(
            homeTeamStats ?? {},
            'ThirdDownConversions',
          )}/${getDisplay(homeTeamStats ?? {}, 'ThirdDownAttempts')}`}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'FourthDownConversions') || getDisplay(homeTeamStats ?? {}, 'FourthDownConversions')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'FourthDownConversions') >
          getValue(homeTeamStats ?? {}, 'FourthDownConversions')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {`${getDisplay(
            awayTeamStats ?? {},
            'FourthDownConversions',
          )}/${getDisplay(awayTeamStats ?? {}, 'FourthDownAttempts')}`}
        </p>
        <p class="text-gray-5">4th Down</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'FourthDownConversions') >
          getValue(awayTeamStats ?? {}, 'FourthDownConversions')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {`${getDisplay(
            homeTeamStats ?? {},
            'FourthDownConversions',
          )}/${getDisplay(homeTeamStats ?? {}, 'FourthDownAttempts')}`}
        </p>
      </div>{/if}

    {#if getDisplay(awayTeamStats ?? {}, 'RedZoneConversions') || getDisplay(homeTeamStats ?? {}, 'RedZoneConversions')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'RedZoneConversions') >
          getValue(homeTeamStats ?? {}, 'RedZoneConversions')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {`${getDisplay(
            awayTeamStats ?? {},
            'RedZoneConversions',
          )}/${getDisplay(awayTeamStats ?? {}, 'RedZoneAttempts')}`}
        </p>
        <p class="text-gray-5">Red Zone</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'RedZoneConversions') >
          getValue(awayTeamStats ?? {}, 'RedZoneConversions')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {`${getDisplay(
            homeTeamStats ?? {},
            'RedZoneConversions',
          )}/${getDisplay(homeTeamStats ?? {}, 'RedZoneAttempts')}`}
        </p>
      </div>
    {/if}
  </div>
</TeamPanel>
<TeamPanel>
  <div
    class="border-t border-gray-6 dark:border-gray-4 divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1"
  >
    {#if getDisplay(awayTeamStats ?? {}, 'Punting-Punts') || getDisplay(homeTeamStats ?? {}, 'Punting-Punts')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'Punting-Punts') >
          getValue(homeTeamStats ?? {}, 'Punting-Punts')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'Punting-Punts')}
        </p>
        <p class="text-gray-5">Punts</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'Punting-Punts') >
          getValue(awayTeamStats ?? {}, 'Punting-Punts')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'Punting-Punts')}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Passing-Interceptions') || getDisplay(homeTeamStats ?? {}, 'Passing-Interceptions') || getDisplay(awayTeamStats ?? {}, 'Fumbles') || getDisplay(homeTeamStats ?? {}, 'Fumbles')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={awayTurnovers < homeTurnovers
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {awayTurnovers}
        </p>
        <p class="text-gray-5">Turnovers</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={homeTurnovers < awayTurnovers
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {homeTurnovers}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Passing-Interceptions') || getDisplay(homeTeamStats ?? {}, 'Passing-Interceptions')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'Passing-Interceptions') <
          getValue(homeTeamStats ?? {}, 'Passing-Interceptions')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'Passing-Interceptions')}
        </p>
        <p class="text-gray-5">Interceptions Thrown</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'Passing-Interceptions') <
          getValue(awayTeamStats ?? {}, 'Passing-Interceptions')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'Passing-Interceptions')}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Fumbles') || getDisplay(homeTeamStats ?? {}, 'Fumbles')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'FumblesLost') <
          getValue(homeTeamStats ?? {}, 'FumblesLost')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'FumblesLost')}
        </p>
        <p class="text-gray-5">Fumbles Lost</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'FumblesLost') <
          getValue(awayTeamStats ?? {}, 'FumblesLost')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'FumblesLost')}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Penalties') || getDisplay(homeTeamStats ?? {}, 'Penalties')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'Penalties') <
          getValue(homeTeamStats ?? {}, 'Penalties')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'Penalties')}
        </p>
        <p class="text-gray-5">Penalties</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'Penalties') <
          getValue(awayTeamStats ?? {}, 'Penalties')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'Penalties')}
        </p>
      </div>
    {/if}
  </div>
</TeamPanel>
<TeamPanel>
  <div
    class="border-t border-gray-6 dark:border-gray-4 divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1"
  >
    {#if getDisplay(awayTeamStats ?? {}, 'Defensive-Sacks') || getDisplay(homeTeamStats ?? {}, 'Defensive-Sacks')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'Defensive-Sacks') >
          getValue(homeTeamStats ?? {}, 'Defensive-Sacks')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'Defensive-Sacks')}
        </p>
        <p class="text-gray-5">Sacks</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'Defensive-Sacks') >
          getValue(awayTeamStats ?? {}, 'Defensive-Sacks')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'Defensive-Sacks')}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Defensive-TacklesForLoss') || getDisplay(homeTeamStats ?? {}, 'Defensive-TacklesForLoss')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={getValue(awayTeamStats ?? {}, 'Defensive-TacklesForLoss') >
          getValue(homeTeamStats ?? {}, 'Defensive-TacklesForLoss')
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(awayTeamStats ?? {}, 'Defensive-TacklesForLoss')}
        </p>
        <p class="text-gray-5">Tackles For Loss</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={getValue(homeTeamStats ?? {}, 'Defensive-TacklesForLoss') >
          getValue(awayTeamStats ?? {}, 'Defensive-TacklesForLoss')
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {getDisplay(homeTeamStats ?? {}, 'Defensive-TacklesForLoss')}
        </p>
      </div>
    {/if}

    {#if getDisplay(awayTeamStats ?? {}, 'Defensive-InterceptionTouchdowns') || getDisplay(homeTeamStats ?? {}, 'Kickoff-ReturnTouchdowns') || getDisplay(awayTeamStats ?? {}, 'Kickoff-ReturnTouchdowns') || getDisplay(homeTeamStats ?? {}, 'Defensive-InterceptionTouchdowns') || getDisplay(awayTeamStats ?? {}, 'PuntReturn-Touchdowns') || getDisplay(homeTeamStats ?? {}, 'PuntReturn-Touchdowns')}
      <div class="flex justify-center relative">
        <p
          class="absolute top-1 left-1 px-2 rounded-2xl"
          style={awayDefensiveTd > homeDefensiveTd
            ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {awayDefensiveTd}
        </p>
        <p class="text-gray-5">D/ST TD</p>
        <p
          class="absolute top-1 right-1 px-2 rounded-2xl"
          style={homeDefensiveTd > awayDefensiveTd
            ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
            : undefined}
        >
          {homeDefensiveTd}
        </p>
      </div>
    {/if}
  </div>
</TeamPanel>
